/**
 * router.tsx - the unified-portal AppRouter wrapper for the SignalSentry SPA.
 *
 * Per ADR-006 section 1 + Phase 3.1 plan 02: routes are registered
 * conditionally based on JWT claims (actor_type + impersonating_as +
 * target_org). The route table is built by the pure buildRoutes(user)
 * function in src/router/build-routes.tsx; this module just memoizes a
 * createBrowserRouter instance keyed on the relevant user shape and
 * remounts on token-swap (Phase 3.1 plan 03).
 *
 * Provider hierarchy (from main.tsx):
 *   QueryClientProvider > AuthProvider > WebSocketProvider > AppRouter
 *
 * The router rebuilds when EITHER actor_type OR impersonating_as flips.
 * That covers:
 *   - login transitions (anon -> client_user / staff)
 *   - logout transitions (any -> anon)
 *   - startImpersonation (staff -> staff impersonating)
 *   - exitImpersonation (staff impersonating -> staff)
 *
 * Modified: 2026-04-25
 */
import { useMemo } from 'react';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { useAuth } from '@/features/auth/useAuth';
import { buildRoutes } from './router/build-routes';

/**
 * AppRouter - top-level routing component.
 *
 * Reads the current user via useAuth and rebuilds the underlying
 * createBrowserRouter only when the relevant claim shape changes. While
 * AuthProvider is initializing (the bootstrap /v1/auth/me roundtrip),
 * this component renders null to avoid a flash of routes that may not
 * be registered post-bootstrap.
 *
 * @returns The RouterProvider for the current user, or null while
 *   AuthProvider is initializing.
 */
export function AppRouter(): JSX.Element | null {
  const { user, status } = useAuth();

  // Avoid a flash of stale routes during bootstrap. AuthProvider transitions
  // to 'authenticated' or 'unauthenticated' deterministically; null is the
  // safest render in the meantime.
  const routerKey = `${user?.actor_type ?? 'anon'}:${user?.impersonating_as ? 'imp' : 'real'}`;

  const router = useMemo(
    () => createBrowserRouter(buildRoutes(user)),
    // Rebuild only when the routing-relevant shape of `user` flips. We do
    // not include the entire user object as a dep; that would rebuild on
    // every /me re-fetch even when the route surface is identical.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- key inputs only
    [routerKey],
  );

  if (status === 'initializing') {
    return null;
  }

  return <RouterProvider router={router} />;
}
